import { useState, useCallback, useEffect } from 'react';

/**
 * useMenuPosition Hook
 * 
 * Handles menu positioning and viewport boundary adjustments.
 * Also handles closing the menu when the page scrolls.
 */
export function useMenuPosition(btnRef, menuRef, showMenu, onClose) {
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  // Reposition menu to stay within viewport
  const repositionMenu = useCallback(() => {
    if (!menuRef.current || !btnRef.current) return;
    
    const menuRect = menuRef.current.getBoundingClientRect();
    const btnRect = btnRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = 8;
    
    let newTop = menuPosition.top;
    let newLeft = menuPosition.left;
    let needsUpdate = false;
    
    // Check bottom overflow
    if (menuRect.bottom > viewportHeight - padding) {
      const topAbove = btnRect.top - menuRect.height - 4;
      if (topAbove >= padding) {
        newTop = topAbove;
      } else {
        newTop = viewportHeight - menuRect.height - padding;
      }
      needsUpdate = true;
    }
    
    // Check top overflow
    if (menuRect.top < padding) {
      newTop = padding;
      needsUpdate = true;
    }
    
    // Check right overflow
    if (menuRect.right > viewportWidth - padding) {
      newLeft = viewportWidth - menuRect.width - padding;
      needsUpdate = true;
    }
    
    // Check left overflow
    if (menuRect.left < padding) {
      newLeft = padding;
      needsUpdate = true;
    }
    
    if (needsUpdate) {
      setMenuPosition({ top: newTop, left: newLeft });
    }
  }, [menuPosition.top, menuPosition.left, btnRef, menuRef]);

  // Close menu when ANY element scrolls (captures scroll in nested containers)
  useEffect(() => {
    if (!showMenu || !onClose) return;

    const handleScroll = (e) => {
      // Don't close if scrolling inside the menu itself
      if (menuRef.current?.contains(e.target)) return;
      
      // Close on any scroll event from any container
      onClose();
    };

    // Use capture phase to catch scroll events from any scrollable container
    // This fires for scrolls on divs, not just the window
    document.addEventListener('scroll', handleScroll, true);
    
    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [showMenu, onClose, menuRef]);

  // Place from the pointer. Measuring the button would rebuild the board picture.
  const openMenu = useCallback((rowCount = 6, event) => {
    setMenuPosition(menuAnchor(rowCount, event, btnRef.current));
  }, [btnRef]);

  // The list is already in the page, hidden. Unhide it here, during the press,
  // so the browser can paint it without waiting for React to render.
  const showNow = useCallback((rowCount, event) => {
    const pos = menuAnchor(rowCount, event, btnRef.current);
    const menu = menuRef.current;
    if (!menu) return pos;
    menu.style.top = `${pos.top}px`;
    menu.style.left = `${pos.left}px`;
    menu.classList.remove('status-dropdown__menu--shut');
    return pos;
  }, [btnRef, menuRef]);

  const commitPosition = useCallback((pos) => {
    setMenuPosition(pos);
  }, []);

  return {
    menuPosition,
    openMenu,
    showNow,
    commitPosition,
    repositionMenu,
  };
}

function menuAnchor(rowCount, event, button) {
  const fromPointer = event && (event.clientX || event.clientY);
  let anchorTop;
  let anchorBottom;
  let anchorLeft;
  if (fromPointer) {
    anchorTop = event.clientY;
    anchorBottom = event.clientY;
    anchorLeft = event.clientX;
  } else if (button) {
    const rect = button.getBoundingClientRect();
    anchorTop = rect.top;
    anchorBottom = rect.bottom;
    anchorLeft = rect.left;
  } else {
    anchorTop = 0;
    anchorBottom = 0;
    anchorLeft = 0;
  }
  const padding = 8;
  const height = 16 + rowCount * 30;
  const width = 200;
  let top = anchorBottom + 4;
  let left = anchorLeft;
  if (top + height > window.innerHeight - padding) {
    const above = anchorTop - height - 4;
    top = above >= padding ? above : Math.max(padding, window.innerHeight - height - padding);
  }
  if (left + width > window.innerWidth - padding) {
    left = Math.max(padding, window.innerWidth - width - padding);
  }
  if (left < padding) left = padding;
  return { top, left };
}

export default useMenuPosition;
